import { RoleId } from '@wix/members-domain-ts';
import { CameraFill } from '@wix/wix-ui-icons-common/on-stage';
import classNames from 'classnames';
import type { ChangeEventHandler } from 'react';
import React, { Component, createRef, Fragment } from 'react';
import { ImageResizeOptions, WowImage } from '@wix/wix-ui-tpa/cssVars';

import AvatarInput from '../../../../../common/components/AvatarInput';
import { BadgeList } from '../../../../../common/components/BadgeList';
import { Blocked } from '../../../../../common/components/Blocked';
import type { ButtonClickHandler } from '../../../../../common/components/Button';
import Button from '../../../../../common/components/Button';
import FollowOrChatButton from '../../../../../common/components/FollowOrChatButton';
import LoaderOverlay from '../../../../../common/components/LoaderOverlay';
import MemberMoreButton from '../../../../../common/components/MemberMoreButton';
import MemberName from '../../../../../common/components/MemberName';
import { EditProfileDetailsModal } from '../../../../../common/components/Modals';
import { RepositionButton } from '../../../../../common/components/RepositionButton';
import RoleIcons from '../../../../../common/components/RoleIcons';
import {
  acceptableImageExtensions,
  imageEncoding,
} from '../../../../../constants/image';
import { ModalsContext } from '../../../../../contexts/modals';
import {
  absorbArguments,
  forwardCoverRepositionAttributes,
  forwardResizedImage,
} from '../../../../../services/event-handler';
import { getElementId } from '../../../../../services/html-attributes';
import { maybeOpenContactsWindow } from '../../../../../services/navigation';
import type { BaseComponentProps } from '../../../../../types';
import { DataHook, ElementId, Position } from '../../../../../types';
import styles from './DesktopNonResponsiveWidget.scss';

interface ButtonProps {
  dataHook?: DataHook;
  title: string;
  isSecondary: boolean;
  opaque?: boolean;
  ariaLabel?: string;
  onClick: ButtonClickHandler;
}

enum BREAKPOINTS {
  NARROW = 659,
  WIDE = 840,
}

const COVER_HEIGHT = 110;
const DEFAULT_FOCAL_POINT = 50;

class DesktopNonResponsiveWidget extends Component<BaseComponentProps> {
  private coverRef = createRef<HTMLDivElement>();
  state = { isMovingCover: false };

  render = () => {
    const { site, globalSettings, computed, profilePage } = this.props;
    const { isDesignPreview, inCommunity, showCover, showAsBlockedByMember } =
      computed;

    if (showAsBlockedByMember) {
      return this.renderEmptyWidget();
    }

    const { isSocial } = site;
    const showFollowers = globalSettings.ffLightboxEnabled
      ? globalSettings.ffLightboxInstalled
      : globalSettings.showFollowers;
    const isFollowersVisible = showFollowers && inCommunity && isSocial;

    const containerClassName = classNames({
      [styles.profile]: true,
      [styles.designPreview]: isDesignPreview || !profilePage.isProfilePreview,
      [styles.withCover]: showCover,
      [styles.editMode]: computed.canEdit && !profilePage.isProfilePreview,
      [styles.nonSocial]: !site.isSocial,
      [this.getWidgetSizeClass()]: true,
    });

    return (
      <div data-hook={DataHook.VerticalWidget}>
        <div className={styles.box}>
          <div className={styles.border} />
          <div className={containerClassName}>
            {this.props.profilePage.isSaving && this.renderLoader()}
            {showCover && this.renderCover()}
            <div className={styles.layout}>
              {this.renderPhoto()}
              <div className={styles.content}>
                <div className={styles.memberDetails}>
                  {this.renderMemberDetails()}
                  {isFollowersVisible && this.renderFollowers()}
                </div>
                {this.renderButtons()}
                {this.renderActions()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  private renderEmptyWidget() {
    const containerClassName = classNames({
      [styles.profile]: true,
      [styles.nonSocial]: true,
      [this.getWidgetSizeClass()]: true,
    });

    return (
      <div data-hook={DataHook.VerticalWidget}>
        <div className={styles.box}>
          <div className={styles.border} />
          <div className={containerClassName}>
            <div className={styles.layout}>
              {this.renderPhoto()}
              <div className={styles.content}>
                <div className={styles.memberDetails}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  private readonly getWidgetSizeClass = () => {
    const { host, computed } = this.props;

    if (computed.isWidgetPlugin) {
      return styles.small;
    }

    if (!host?.dimensions?.width) {
      return styles.fullWidth;
    }

    if (host?.dimensions.width <= BREAKPOINTS.NARROW) {
      return styles.small;
    }

    if (host?.dimensions.width < BREAKPOINTS.WIDE) {
      return styles.medium;
    }

    return styles.large;
  };

  private readonly renderLoader = () => (
    <LoaderOverlay className={styles.loader} />
  );

  private readonly handleCoverPositionChange = (event: MouseEvent) => {
    const { host, handlers } = this.props;
    const { changeCoverPosition } = handlers;
    const coverWidth = host?.dimensions?.width ?? 0;
    forwardCoverRepositionAttributes(changeCoverPosition, coverWidth)(event);
  };

  private readonly startRepositioningCover = () => {
    const { profilePage } = this.props;
    const { isCoverRepositionMode } = profilePage;

    if (isCoverRepositionMode && this.coverRef.current) {
      this.coverRef.current.addEventListener(
        'mousemove',
        this.handleCoverPositionChange,
      );
      this.setState({ isMovingCover: true });
    }
  };

  private readonly stopRepositioningCover = () => {
    const { profilePage } = this.props;
    const { isCoverRepositionMode } = profilePage;
    if (isCoverRepositionMode && this.coverRef.current) {
      this.coverRef.current.removeEventListener(
        'mousemove',
        this.handleCoverPositionChange,
      );
      this.setState({ isMovingCover: false });
    }
  };

  private readonly renderCoverWowImage = () => {
    const { computed } = this.props;
    const {
      cardProfileCoverPhoto,
      isDesignBackgroundsAndBordersPreview,
      defaultCoverUrl,
    } = computed;
    const coverUrl =
      isDesignBackgroundsAndBordersPreview && defaultCoverUrl
        ? defaultCoverUrl
        : cardProfileCoverPhoto?.src;

    return (
      <>
        <WowImage
          src={coverUrl ?? ''}
          className={styles.coverImage}
          data-hook={DataHook.HighQualityCover}
          focalPoint={{
            x: cardProfileCoverPhoto?.position.x ?? DEFAULT_FOCAL_POINT,
            y: cardProfileCoverPhoto?.position.y ?? DEFAULT_FOCAL_POINT,
          }}
          resize={ImageResizeOptions.cover}
          sourceWidth={cardProfileCoverPhoto?.imageProps.rw}
          sourceHeight={COVER_HEIGHT}
          height={COVER_HEIGHT}
          shouldUseLQIP
          encoding={imageEncoding}
        />
      </>
    );
  };

  private readonly renderCover = () => {
    const { compId, profilePage, handlers, computed, t } = this.props;
    const { isCoverRepositionMode } = profilePage;
    const { cardProfileCoverPhotoStyle, showCoverPhoto } = computed;
    const coverPhotoInputId = getElementId(compId, ElementId.CoverPhotoInput);
    const { isMovingCover } = this.state;

    const { enterCoverRepositionMode, setEditCover } = handlers;

    const handleCoverUpload: ChangeEventHandler<HTMLInputElement> = (event) => {
      enterCoverRepositionMode();
      forwardResizedImage(setEditCover)(event);
    };
    const canEdit =
      computed.canEdit && !isCoverRepositionMode && showCoverPhoto;

    if (!isCoverRepositionMode) {
      return (
        <>
          <div
            data-hook={DataHook.CoverWrapper}
            className={classNames(styles.coverWrapper, styles.coverColor)}
          >
            <div
              className={classNames(styles.coverBorderMargin)}
              ref={this.coverRef}
              onMouseDown={this.startRepositioningCover}
              onMouseUp={this.stopRepositioningCover}
              onMouseLeave={this.stopRepositioningCover}
            >
              {this.renderCoverWowImage()}
              {profilePage.isCoverLoading && this.renderLoader()}
              {canEdit && (
                <div className={styles.expCoverPhotoContainer}>
                  <input
                    id={coverPhotoInputId}
                    className={styles.uploadInput}
                    type="file"
                    accept={acceptableImageExtensions}
                    aria-label={t('profile-widget.change-aria-label-photo')}
                    onChange={handleCoverUpload}
                  />
                  <label
                    htmlFor={coverPhotoInputId}
                    className={styles.expUploadLabel}
                  />
                  <div className={styles.coverPhotoChange}>
                    <CameraFill className={styles.coverPhotoUploadIcon} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <div
          data-hook={DataHook.CoverWrapper}
          className={classNames(styles.coverWrapper, styles.coverColor)}
        >
          <div
            className={classNames(
              styles.coverBorderMargin,
              isCoverRepositionMode && styles.onTop,
            )}
            ref={this.coverRef}
            onMouseDown={this.startRepositioningCover}
            onMouseUp={this.stopRepositioningCover}
            onMouseLeave={this.stopRepositioningCover}
          >
            <div
              data-hook={DataHook.HighQualityCover}
              style={cardProfileCoverPhotoStyle || {}}
              className={classNames(
                styles.cover,
                !cardProfileCoverPhotoStyle && styles.coverColor,
              )}
            >
              {!cardProfileCoverPhotoStyle && (
                <div
                  className={classNames(styles.overlay, styles.coverOverlay)}
                />
              )}
              <div className={classNames(styles.overlay)} />
              {profilePage.isCoverLoading && this.renderLoader()}
              {canEdit && (
                <div className={styles.coverPhotoContainer}>
                  <input
                    id={coverPhotoInputId}
                    className={styles.uploadInput}
                    type="file"
                    accept={acceptableImageExtensions}
                    aria-label={t('profile-widget.change-aria-label-photo')}
                    onChange={handleCoverUpload}
                  />
                  <label
                    htmlFor={coverPhotoInputId}
                    className={styles.uploadLabel}
                  />
                  {!isCoverRepositionMode && (
                    <div className={styles.coverPhotoChange}>
                      <div className={styles.coverPhotoIco} />
                    </div>
                  )}
                </div>
              )}
              {isCoverRepositionMode && (
                <RepositionButton
                  dataHook={DataHook.DragToChangeCoverPositionCTA}
                  className={classNames(
                    styles.repositionCoverButton,
                    isMovingCover && styles.isMoving,
                  )}
                  text={t('profile-widget.drag-to-reposition')}
                />
              )}
            </div>
          </div>
        </div>
        {isCoverRepositionMode && this.renderRepositionControls()}
      </>
    );
  };

  private renderRepositionControls() {
    const { handlers, t } = this.props;
    const { leaveCoverRepositionMode, saveCoverReposition } = handlers;
    return (
      <div className={styles.repositionControlButtons}>
        {this.renderButton({
          dataHook: DataHook.CardCoverCancel,
          title: t('profile-widget.cancel'),
          isSecondary: true,
          opaque: true,
          ariaLabel: t('profile-widget.close.aria-label'),
          onClick: absorbArguments(leaveCoverRepositionMode),
        })}
        {this.renderButton({
          dataHook: DataHook.CardCoverSave,
          title: t('profile-widget.save'),
          isSecondary: false,
          onClick: absorbArguments(saveCoverReposition),
        })}
      </div>
    );
  }

  private readonly renderPhoto = () => {
    const { member, profilePage, computed, handlers } = this.props;
    const { editPicture, isProfilePreview } = profilePage;
    const { canEdit, profileLayout, showCover, profileImageSize } = computed;
    const handlePhotoUpload = forwardResizedImage(handlers.setMemberPicture);

    const avatarInputClasses = {
      [styles.withNotCover]: !showCover,
    };

    return (
      <AvatarInput
        className={classNames(avatarInputClasses)}
        url={editPicture?.file ?? member.picture}
        onClick={handlePhotoUpload}
        canUpload={!isProfilePreview && canEdit}
        profileLayout={profileLayout}
        imageSize={profileImageSize}
      />
    );
  };

  private readonly renderBadges = () => {
    const { site, computed, handlers } = this.props;
    const { navigateToViewedMember } = handlers;

    return (
      <div className={styles.badgesContainer}>
        <BadgeList
          {...computed.badgesSettings}
          onClickMore={site.isSocial ? navigateToViewedMember : undefined}
          onBadgeClick={site.isSocial ? navigateToViewedMember : undefined}
          maxRows={3}
        />
      </div>
    );
  };

  private readonly renderMemberDetails = () => {
    const { member, rolesMap, site, t, computed } = this.props;
    const {
      badgesSettings,
      showTitle,
      showAsBlocked,
      showRoleLabel,
      memberNameHtmlTags,
    } = computed;
    const hasBadges = badgesSettings.badges.length > 0;

    return (
      <div data-hook={DataHook.MemberName} className={styles.nameBox}>
        <MemberName
          name={member.name}
          className={styles.nameBoxText}
          tooltipClass={styles.tooltipWrapper}
          htmlTag={memberNameHtmlTags.nonResponsive}
        />
        {site.isSocial && showRoleLabel && (
          <RoleIcons
            containerClassName={styles.roleIconsContainer}
            iconClassName={styles.roleIcon}
            roles={member.roles}
            rolesMap={rolesMap}
            withNames={true}
            t={t}
          />
        )}
        {showTitle && this.renderTitle()}
        {site.isSocial && showAsBlocked && (
          <Blocked className={styles.blocked} />
        )}
        {hasBadges && this.renderBadges()}
      </div>
    );
  };

  private readonly renderTitle = () => {
    const { member, computed, t } = this.props;
    const { isDesignPreview, isDisplayPreview } = computed;
    const title = member.title;
    const shouldShowTitlePreview =
      (isDesignPreview || isDisplayPreview) && !title;

    return (
      <Fragment>
        {title && (
          <div className={styles.titleWrapper}>
            <p className={classNames(styles.title)}>{title}</p>
          </div>
        )}
        {shouldShowTitlePreview && (
          <div className={styles.titleWrapper}>
            <p className={classNames(styles.title)}>
              {t<string>('design-tab.title-preview')}
            </p>
          </div>
        )}
      </Fragment>
    );
  };

  private readonly renderFollowers = () => {
    const { member, globalSettings, handlers, computed } = this.props;
    const { ffLightboxEnabled, ffLightboxInstalled } = globalSettings;
    const { followersInstalled, profileWidgetTexts } = computed;

    const isCTADisabled = !ffLightboxEnabled && !followersInstalled;

    const handleFollowersBoxClick = absorbArguments(() => {
      if (ffLightboxInstalled) {
        return handlers.showFollowers();
      }

      handlers.navigateToViewedMemberFFPage(true);
    });

    const handleFollowingBoxClick = absorbArguments(() => {
      if (ffLightboxInstalled) {
        return handlers.showFollowing();
      }

      handlers.navigateToViewedMemberFFPage(false);
    });

    return (
      <div
        data-hook={DataHook.FollowersFollowing}
        className={styles.numbersBox}
      >
        <div
          data-hook={DataHook.Followers}
          className={styles.followersBoxContainer}
        >
          <Button
            className={classNames(
              styles.commonBtn,
              styles.numbersBoxFollowers,
              {
                [styles.disabled]: isCTADisabled,
              },
            )}
            onClick={handleFollowersBoxClick}
            isDisabled={isCTADisabled}
          >
            <span>{member.followerCount}</span>
            <div className={styles.numbersBoxText}>
              {profileWidgetTexts.followersCountersText}
            </div>
          </Button>
        </div>
        <div className={styles.divider} />
        <div data-hook={DataHook.Following}>
          <Button
            className={classNames(
              styles.commonBtn,
              styles.numbersBoxFollowing,
              {
                [styles.disabled]: isCTADisabled,
              },
            )}
            onClick={handleFollowingBoxClick}
            isDisabled={isCTADisabled}
          >
            <span>{member.followingCount}</span>
            <div className={styles.numbersBoxText}>
              {profileWidgetTexts.followingCountersText}
            </div>
          </Button>
        </div>
      </div>
    );
  };

  private readonly renderFollowOrChatButton = () => {
    const { member, profilePage, computed, handlers } = this.props;
    const { allowChat, showFollowButton, profileWidgetTexts, isDesignPreview } =
      computed;
    const handleFollowButtonClick = absorbArguments(handlers.followOrUnfollow);
    const handleChatButtonClick = absorbArguments(handlers.openChat);

    return (
      <FollowOrChatButton
        dataHook={DataHook.FollowCTA}
        size="medium"
        followText={profileWidgetTexts.followButtonText}
        unfollowText={profileWidgetTexts.followingButtonText}
        chatText={profileWidgetTexts.messageButtonText}
        isFollowing={member.isSubscribed}
        isDisabled={profilePage.isProfilePreview}
        shouldShowChatButton={allowChat || isDesignPreview}
        onFFClick={handleFollowButtonClick}
        onChatClick={handleChatButtonClick}
        showFollowButton={showFollowButton}
      />
    );
  };

  private readonly renderButton = ({
    dataHook,
    title,
    isSecondary,
    opaque,
    onClick,
    ariaLabel,
  }: ButtonProps) => {
    const baseClassName = classNames(
      styles.commonBtn,
      styles.mainButtonElement,
      styles.truncateText,
    );
    const buttonClassName = isSecondary
      ? classNames(
          baseClassName,
          styles.buttonSecondary,
          opaque && styles.opaque,
        )
      : classNames(baseClassName, styles.buttonPrimary);

    return (
      <Button
        dataHook={dataHook}
        className={buttonClassName}
        onClick={onClick}
        ariaLabel={ariaLabel}
      >
        {title}
      </Button>
    );
  };

  private readonly renderOwnProfileButtons = () => {
    const { computed, t } = this.props;
    const { showEditProfileDetailsButton } = computed;

    return showEditProfileDetailsButton ? (
      <div className={classNames(styles.buttonsBox, styles.buttonsBoxVertical)}>
        {showEditProfileDetailsButton && (
          <ModalsContext.Consumer>
            {(modalsContext) => (
              <div className={styles.mainButton}>
                {this.renderButton({
                  dataHook: DataHook.EditProfileDetailsCTA,
                  title: t('profile-widget.edit-details'),
                  isSecondary: true,
                  onClick: () => {
                    modalsContext?.showModal(EditProfileDetailsModal);
                  },
                })}
              </div>
            )}
          </ModalsContext.Consumer>
        )}
      </div>
    ) : null;
  };

  private readonly renderButtons = () => {
    const { profilePage, site, computed } = this.props;
    const { isSocial } = site;
    const { isProfilePreview } = profilePage;
    const { canEdit, isDesignPreview } = computed;

    const isDesignPreviewAndSocial = isDesignPreview && isSocial;

    if (canEdit && !isProfilePreview && !isDesignPreview) {
      return this.renderOwnProfileButtons();
    }

    return !canEdit || isProfilePreview || isDesignPreviewAndSocial ? (
      <div className={styles.buttonsBox}>
        <div className={styles.mainButton}>
          {this.renderFollowOrChatButton()}
        </div>
      </div>
    ) : null;
  };

  private readonly renderActions = () => {
    const { member, isMobile, isRTL, computed, handlers } = this.props;
    const { isProfilePreview } = this.props.profilePage;
    const { additionalActions, rolesActions, isResponsiveEditor } = computed;
    const { uid } = member;

    if (rolesActions.length === 0 && additionalActions.length === 0) {
      return;
    }

    const handleRoleAction = (roleId: RoleId) => {
      if (roleId === RoleId.CONTACT_PAGE) {
        const { metaSiteId } = this.props;
        return maybeOpenContactsWindow(metaSiteId, uid, isResponsiveEditor);
      }

      handlers.executeRoleAction(roleId);
    };

    return (
      <MemberMoreButton
        containerClass={styles.moreActions}
        additionalActions={additionalActions}
        rolesActions={rolesActions}
        alignment={Position.Right}
        isRtlLocale={isRTL}
        isMobile={isMobile}
        isDisabled={isProfilePreview}
        onAdditionalAction={handlers.executeAdditionalAction}
        onRoleAction={handleRoleAction}
      />
    );
  };
}

export default DesktopNonResponsiveWidget;
